import { useEffect, useState } from "react";
import { api, StreamVideo } from "../lib/api";
import { GENERATION_LANGS, languageName } from "../lib/languages";

/**
 * Source-language control on VideoDetail. Two states:
 *   - unset: amber banner prompting the user to pick one (the generation
 *     panel stays hidden until then).
 *   - set: inline editor so the user can fix a wrong choice.
 *
 * Changing the source language after generations exist doesn't rewrite
 * those jobs — each row keeps the source it was generated against. The
 * new value only applies to future generations.
 */
export function SourceLangBanner({
  video,
  onSaved,
}: {
  video: StreamVideo;
  onSaved: () => void;
}) {
  const current = video.meta.source_lang ?? "";
  const [lang, setLang] = useState<string>(current || "de");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep the dropdown in sync if the parent re-fetches the video.
  useEffect(() => {
    if (current) setLang(current);
  }, [current]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.updateVideo(video.uid, { meta: { source_lang: lang } });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (!current) {
    return (
      <section className="card p-4 border-amber-300 bg-amber-50 text-amber-900">
        <div className="text-sm font-medium mb-1">Source language not set</div>
        <p className="text-xs mb-3">
          Auto-generation needs to know the language spoken in this video.
          Pick it once and we'll remember.
        </p>
        <div className="flex gap-2 items-center">
          <select
            className="input"
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            disabled={saving}
          >
            {GENERATION_LANGS.map((code) => (
              <option key={code} value={code}>{languageName(code)} — {code}</option>
            ))}
          </select>
          <button className="btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
        {error && <p className="text-xs text-red-700 mt-2">{error}</p>}
      </section>
    );
  }

  const dirty = lang !== current;
  return (
    <section className="card p-4">
      <label className="label">Source language</label>
      <div className="flex gap-2 items-center">
        <select
          className="input"
          value={lang}
          onChange={(e) => setLang(e.target.value)}
          disabled={saving}
        >
          {GENERATION_LANGS.map((code) => (
            <option key={code} value={code}>{languageName(code)} — {code}</option>
          ))}
        </select>
        <button
          className="btn-primary"
          onClick={save}
          disabled={saving || !dirty}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <p className="text-xs text-neutral-500 mt-2">
        The language spoken in the video. Used when transcribing for dubs and
        subtitles. Changing this won't affect dubs that have already been generated.
      </p>
      {error && <p className="text-xs text-red-700 mt-2">{error}</p>}
    </section>
  );
}
